/**
 * La Home (03-SCHERMATE-TV §3.1): **quali fasce, in che ordine, e con cosa dentro**.
 *
 * ## L'ordine lo decide il backend, il contenuto no
 *
 * La vetrina (`GET /api/v1/app/storefront`) dice «qui va una fascia di eventi di calcio, in terza
 * posizione». Non dice quali partite. Il contenuto lo mette insieme l'app con quello che ha già
 * scaricato per altri motivi — il catalogo, l'EPG, l'avanzamento — e questo è il punto di tutta
 * l'architettura: un operatore che riordina la sua home non fa ripubblicare l'app sullo store.
 *
 * ## Un tipo sconosciuto si salta
 *
 * `type` è una stringa, non un'unione chiusa. Un backend più nuovo pubblica una fascia che questa
 * versione non sa disegnare, e su un parco di televisori che si aggiornano quando gli pare è la
 * condizione **normale**. Saltarla costa una fascia in meno; rifiutare la vetrina costa la home.
 *
 * ## Una fascia vuota non si disegna
 *
 * Vale per tutte, e non è un dettaglio estetico: «Continua a guardare» vuota insegna che quella riga
 * è finta, e chi apre l'app per la prima volta le avrebbe vuote quasi tutte.
 */

import { compareCatalogNames } from './catalogSort.js';
import { playableChannelOf, type SportEventView } from './sportEvents.js';
import { continueWatching, latestArrivals, recommendations, watchStateOf, type VodItem, type WatchProgress, type WatchState } from './catalog.js';
import type { Channel, Program } from './guide.js';

/** Quanti elementi entrano in una fascia. Oltre, c'è «Vedi tutti». */
export const HOME_ROW_LIMIT = 20;

/**
 * Quali tipologie di evento chiedere al gateway, per questa vetrina.
 *
 * ## Due sorgenti, e la seconda è quella che mancava
 *
 * Le fasce `events` dicono la loro tipologia in `params.kind`: quelle sono ovvie. Gli **hero** su un
 * evento no — portano l'identificativo dell'evento e basta — e senza sapere a quale chiave chiedere
 * non si possono risolvere. Finché questa funzione non esisteva, un hero su una partita compariva
 * solo se c'era *anche* una fascia che nominava per caso la stessa tipologia; altrimenti spariva, e
 * da fuori si leggeva come «il pannello non ha salvato».
 *
 * ## Perché non sempre tutte
 *
 * Ogni tipologia è una richiesta sullo stesso gateway che conta gli accessi per indirizzo. Chi non ha
 * hero su eventi — cioè quasi tutti — non deve pagarne nessuna.
 *
 * Sta nel dominio e non nella schermata perché è una **decisione**, e una decisione presa dentro un
 * `useEffect` si prova solo montando un componente: qui si prova con un oggetto.
 */
export function eventKindsToFetch(storefront: {
  readonly modules: ReadonlyArray<{ readonly type: string; readonly params: Readonly<Record<string, unknown>> }>;
  readonly heroes: ReadonlyArray<{ readonly kind: string }>;
  readonly eventKinds: ReadonlyArray<string>;
}): ReadonlyArray<string> {
  const kinds = new Set<string>();

  for (const module of storefront.modules) {
    if (module.type !== 'events') continue;
    const raw = module.params['kind'];
    if (typeof raw === 'string' && raw.trim() !== '') kinds.add(raw.trim().toLowerCase());
  }

  const needsEvents = storefront.heroes.some(
    (hero) => hero.kind === 'live_event' || hero.kind === 'scheduled_event',
  );
  if (needsEvents) {
    for (const code of storefront.eventKinds) {
      const normalized = code.trim().toLowerCase();
      if (normalized !== '') kinds.add(normalized);
    }
  }

  return [...kinds];
}

/** I tipi di fascia che questa versione sa disegnare. Gli altri si saltano. */
export type HomeRowKind = 'continue' | 'recs' | 'latest' | 'picks' | 'onair' | 'cats' | 'aggs' | 'events';

export interface HomeVodEntry {
  readonly item: VodItem;
  readonly state: WatchState;
  /** Il perché del consiglio, o da quanto è in catalogo. `null` quando non c'è niente da dire. */
  readonly because: string | null;
}

/** Un canale con il suo programma in corso, per la fascia «In onda ora». */
export interface HomeOnAirEntry {
  readonly channel: Channel;
  readonly now: Program | null;
  readonly next: Program | null;
  /** Quanto è passato del programma in corso, 0–100. `null` se non c'è un programma. */
  readonly percent: number | null;
}

export interface HomeCategoryEntry {
  readonly id: string;
  readonly name: string;
  readonly channelCount: number;
}

export interface HomeAggregatorEntry {
  readonly id: string;
  readonly label: string;
  readonly color: string;
  /** Lo sfondo della piastrella, o `null`. Il colore resta sotto: vedi `StorefrontAggregator`. */
  readonly imageUrl: string | null;
}

export type HomeRow =
  | { readonly kind: 'continue' | 'recs' | 'latest' | 'picks'; readonly title: string | null; readonly items: ReadonlyArray<HomeVodEntry> }
  | { readonly kind: 'onair'; readonly title: string | null; readonly channels: ReadonlyArray<HomeOnAirEntry> }
  | { readonly kind: 'cats'; readonly title: string | null; readonly categories: ReadonlyArray<HomeCategoryEntry> }
  | { readonly kind: 'aggs'; readonly title: string | null; readonly aggregators: ReadonlyArray<HomeAggregatorEntry> }
  | { readonly kind: 'events'; readonly title: string | null; readonly events: ReadonlyArray<HomeEventEntry> };

/**
 * Un evento in una fascia, **con il canale su cui questa linea può guardarlo**.
 *
 * `channel` è `null` quando la linea non possiede nessuno dei canali su cui va in onda, ed è il
 * caso normale e frequente: `app:events:<tipologia>` è una chiave sola per tutta l'installazione, e
 * porta i canali di **tutti** i pacchetti.
 *
 * La decisione sta qui e non nella schermata perché è una decisione — vedi `playableChannelOf`. La
 * schermata prima faceva `channelIds[0]`, cioè apriva il primo che il server aveva messo in fila,
 * e su una linea che non lo vende il risultato era una card che si disegna, dice «in diretta», e
 * premendo OK non fa niente.
 */
export interface HomeEventEntry {
  readonly view: SportEventView;
  readonly channel: { readonly id: string; readonly name: string; readonly number: number } | null;
}

export interface HomeInput {
  /** I moduli come li ha pubblicati l'operatore: l'ordine è il loro. */
  readonly modules: ReadonlyArray<{ readonly type: string; readonly title: string | null; readonly params: Readonly<Record<string, unknown>> }>;
  readonly aggregators: ReadonlyArray<HomeAggregatorEntry>;
  readonly vod: ReadonlyArray<VodItem>;
  readonly progress: WatchProgress;
  readonly recentIds: ReadonlyArray<string>;
  readonly channels: ReadonlyArray<Channel>;
  /** I programmi già scaricati, per canale. */
  readonly programsByChannel: ReadonlyMap<string, ReadonlyArray<Program>>;
  readonly categoryNames: ReadonlyMap<string, string>;
  /**
   * Gli eventi sportivi già chiesti al gateway, per tipologia.
   *
   * **Non si leggono dall'EPG.** Ci avevo provato, confrontando `Program.eventKind` con la tipologia
   * del modulo: quel campo è `null` su ogni programma — il livello compatibile non lo manda e il
   * lettore lo scrive fisso — quindi la fascia non corrispondeva **mai**, e siccome le fasce vuote
   * non si disegnano spariva senza dire niente.
   *
   * La strada giusta esisteva già e non la percorreva nessuno: `GET /api/v1/app/events/:kind`, che
   * il gateway serve dagli eventi in Redis, con l'istante del server dentro. Chi sta sopra la chiama
   * e mette qui il risultato: il dominio non fa richieste.
   */
  readonly eventsByKind: ReadonlyMap<string, ReadonlyArray<SportEventView>>;
  readonly nowMs: number;
}

/** Il programma in onda su un canale, e quello dopo. */
export function onAirOf(
  programs: ReadonlyArray<Program>,
  nowMs: number,
): { now: Program | null; next: Program | null } {
  let now: Program | null = null;
  let next: Program | null = null;
  for (const program of programs) {
    if (program.startMs <= nowMs && nowMs < program.endMs) now = program;
    else if (program.startMs > nowMs && (next === null || program.startMs < next.startMs)) next = program;
  }
  return { now, next };
}

function percentOf(program: Program | null, nowMs: number): number | null {
  if (program === null) return null;
  const span = program.endMs - program.startMs;
  // Una durata nulla o negativa arriva davvero, da EPG scritti male: dividerci darebbe `Infinity`
  // o `NaN`, che diventa una barra larga quanto lo schermo o nessuna barra senza dire perché.
  if (span <= 0) return null;
  const done = ((nowMs - program.startMs) / span) * 100;
  return Math.max(0, Math.min(100, Math.round(done)));
}

function decorate(item: VodItem, progress: WatchProgress, because: string | null = null): HomeVodEntry {
  return { item, state: watchStateOf(item, progress), because };
}

/**
 * «In onda ora»: i canali con qualcosa in corso, **in ordine di numero**.
 *
 * Solo quelli per cui si ha davvero il programma: un canale con la riga vuota in questa fascia non
 * dice «non c'è EPG», dice «non c'è niente in onda», che è falso. I canali senza EPG restano nella
 * guida, dove il vuoto si legge per quello che è.
 */
export function onAirRow(
  input: HomeInput,
  limit: number = HOME_ROW_LIMIT,
  /** `params.categoryId`: la fascia si restringe a una categoria di canali. */
  categoryId: string | null = null,
): ReadonlyArray<HomeOnAirEntry> {
  const out: HomeOnAirEntry[] = [];
  for (const channel of input.channels) {
    if (categoryId !== null && channel.categoryId !== categoryId) continue;
    const programs = input.programsByChannel.get(channel.id);
    if (programs === undefined || programs.length === 0) continue;
    const { now, next } = onAirOf(programs, input.nowMs);
    if (now === null) continue;
    out.push({ channel, now, next, percent: percentOf(now, input.nowMs) });
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Le categorie con quanti canali hanno, **nell'ordine in cui sono arrivate**.
 *
 * Che è `categories.position`, quel che l'operatore trascina nel pannello: `categoryNames` è una
 * `Map` e una `Map` conserva l'ordine d'inserimento. Stessa regola della guida e della vetrina on
 * demand — l'ordine è dichiarato a monte e non si ricalcola a valle.
 */
export function categoryRow(input: HomeInput, limit: number = HOME_ROW_LIMIT): ReadonlyArray<HomeCategoryEntry> {
  const counts = new Map<string, number>();
  for (const channel of input.channels) {
    if (channel.categoryId === '') continue;
    counts.set(channel.categoryId, (counts.get(channel.categoryId) ?? 0) + 1);
  }
  const out: HomeCategoryEntry[] = [];
  for (const [id, name] of input.categoryNames) {
    const channelCount = counts.get(id) ?? 0;
    // Una categoria senza canali per **questa linea** non si mostra: porterebbe a una guida vuota.
    if (channelCount === 0) continue;
    if (name === '') continue;
    out.push({ id, name, channelCount });
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Quanti elementi entrano in una fascia: quel che dice l'operatore, entro un tetto.
 *
 * `params.limit` lo si imposta nel pannello su sette tipi di fascia su otto, e **il player non lo
 * leggeva**: si chiedeva «mostra 5 titoli» e se ne vedevano venti. Non falliva niente, taceva.
 *
 * Il tetto resta perché una fascia è una fila che si scorre con un telecomando: `limit: 500` non è
 * una configurazione, è un modo di rendere la home inusabile. E un valore che non è un numero
 * positivo si ignora invece di produrre una fascia vuota — un `0` scritto per sbaglio nasconderebbe
 * la fascia senza che nessuno capisca perché.
 */
export function limitOf(params: Readonly<Record<string, unknown>>, fallback = HOME_ROW_LIMIT): number {
  const raw = params['limit'];
  const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  if (!Number.isFinite(value) || value < 1) return fallback;
  return Math.min(Math.floor(value), HOME_ROW_LIMIT);
}

/** Il valore testuale di un parametro, o `null`. Vuoto e assente sono la stessa cosa. */
function textParam(params: Readonly<Record<string, unknown>>, name: string): string | null {
  const raw = params[name];
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Le fasce della home, nell'ordine della vetrina.
 *
 * Le vuote non entrano; i tipi sconosciuti si saltano. Il risultato può essere **vuoto**, e chi
 * disegna deve dirlo — una home senza fasce non è una home neutra, è uno schermo bianco.
 */
export function homeRows(input: HomeInput): ReadonlyArray<HomeRow> {
  const rows: HomeRow[] = [];

  for (const module of input.modules) {
    const title = module.title;
    // `params.limit`, quando l'operatore l'ha scritto. Vedi `limitOf`.
    const limit = limitOf(module.params);
    switch (module.type) {
      case 'continue': {
        const items = continueWatching(input.vod, input.progress, input.recentIds).slice(0, limit);
        if (items.length > 0) {
          rows.push({ kind: 'continue', title, items: items.map((item) => decorate(item, input.progress)) });
        }
        break;
      }
      case 'recs': {
        const suggestions = recommendations(input.vod, input.progress).slice(0, limit);
        if (suggestions.length > 0) {
          rows.push({
            kind: 'recs',
            title,
            // Il **titolo** che ha generato il consiglio, non una frase: la frase la compone l'i18n,
            // perché «Perché hai visto X» non si dice uguale in sette lingue.
            items: suggestions.map((entry) => decorate(entry.item, input.progress, entry.becauseOfTitle)),
          });
        }
        break;
      }
      case 'latest': {
        /*
          `params.genre` restringe gli ultimi arrivi a un genere.

          Il confronto è **senza maiuscole e senza spazi ai bordi**: il campo nel pannello è libero, e
          «Azione » scritto con uno spazio non deve dare una fascia vuota che si legge come «non è
          arrivato niente».
        */
        const genre = textParam(module.params, 'genre');
        const pool =
          genre === null
            ? input.vod
            : input.vod.filter((item) => item.genre.trim().toLowerCase() === genre.toLowerCase());
        const items = latestArrivals(pool).slice(0, limit);
        if (items.length > 0) {
          rows.push({ kind: 'latest', title, items: items.map((item) => decorate(item, input.progress)) });
        }
        break;
      }
      case 'onair': {
        const channels = onAirRow(input, limit, textParam(module.params, 'categoryId'));
        if (channels.length > 0) rows.push({ kind: 'onair', title, channels });
        break;
      }
      case 'cats': {
        const categories = categoryRow(input, limit);
        if (categories.length > 0) rows.push({ kind: 'cats', title, categories });
        break;
      }
      /*
        Una fascia curata a mano: `params.ids` elenca i contenuti, e l'**ordine è quello scritto**.

        Non si riordina per data né per titolo: se un operatore ha messo tre film in un ordine, quello
        è il senso della fascia. Gli identificativi che non stanno nel catalogo di questa linea si
        saltano — un titolo scelto dall'operatore può non essere nel pacchetto di chi guarda, e una
        piastrella che apre il vuoto è peggio di una fascia più corta.
      */
      case 'picks': {
        const ids = Array.isArray(module.params['ids']) ? (module.params['ids'] as unknown[]) : [];
        const byId = new Map(input.vod.map((item) => [item.id, item]));
        const items: HomeVodEntry[] = [];
        for (const raw of ids) {
          if (typeof raw !== 'string') continue;
          const item = byId.get(raw);
          if (item === undefined) continue;
          items.push(decorate(item, input.progress));
          if (items.length >= limit) break;
        }
        if (items.length > 0) rows.push({ kind: 'picks', title, items });
        break;
      }
      case 'aggs': {
        if (input.aggregators.length > 0) {
          rows.push({ kind: 'aggs', title, aggregators: input.aggregators });
        }
        break;
      }
      case 'events': {
        const kind = textParam(module.params, 'kind');
        if (kind === null) break;
        // Già ordinati e già filtrati dal dominio degli eventi: `upcomingEvents` toglie i finiti e
        // mette i live davanti, con l'istante **del server** e non l'orologio dell'apparecchio.
        const events = (input.eventsByKind.get(kind.toLowerCase()) ?? [])
          .slice(0, limit)
          // Il canale si risolve **qui**: la fascia la disegna chi non sa quali canali ha la linea.
          .map((view) => ({ view, channel: playableChannelOf(view.event, input.channels) }));
        if (events.length > 0) rows.push({ kind: 'events', title, events });
        break;
      }
      default:
        // Un tipo che questa versione non conosce. Vedi la nota in testa al modulo.
        break;
    }
  }

  return rows;
}

/** Le categorie in ordine alfabetico, dove serve un indice invece di una vetrina. */
export function sortCategoriesByName(
  categories: ReadonlyArray<HomeCategoryEntry>,
): ReadonlyArray<HomeCategoryEntry> {
  return [...categories].sort((left, right) => compareCatalogNames(left.name, right.name));
}
